import { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress, Pagination, Tooltip, InputBase, useTheme, useMediaQuery,
} from '@mui/material';
import { Refresh, ManageAccounts, ToggleOnOutlined, ToggleOffOutlined, TuneOutlined, ChevronRightRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllAccounts, adminAdjustBalance, toggleAccountStatus } from 'api/wallet';
import { useTranslation } from 'react-i18next';
import { user as getUser } from 'localStorage';
import { hasPageAccess } from 'utils/pagePermissions';
import { useUserRefreshTick } from 'hooks/useUserRefreshTick';

// Bento-family redesign — same hand-rolled design language as scenes/branchCash/*
// and scenes/financial/wallets.jsx (kept consistent by copying their local token
// constants rather than importing components/bento/*, which those pages don't use either).
const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const CURRENCIES = ['THB', 'LAK', 'USD'];
const CUR_SYM = { THB: '฿', LAK: '₭', USD: '$' };
const STATUSES = ['all', 'active', 'inactive'];
const AVATARS = ['#6d28d9', '#7c3aed', '#5b21b6', '#4338ca', '#3730a3', '#8b5cf6', '#7e22ce'];
const CARD_SHADOW = '0 1px 2px rgba(20,18,30,.04),0 16px 32px -24px rgba(20,18,30,.18)';
const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const initials = (name) => {
  const p = (name || '?').trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : (name || '?').slice(0, 2)).toUpperCase();
};
const formatDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const labelSx = { fontSize: '10.5px', letterSpacing: '.14em', color: '#a8a3b4', fontWeight: 700 };
const headCell = { fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: '#a8a3b4', textTransform: 'uppercase' };
const GRID = '2.2fr 1fr 1fr 1fr 1.1fr 1fr';

function Kpi({ label, value, color, highlight }) {
  return (
    <Box sx={{
      background: highlight ? 'linear-gradient(155deg,#fff 0%,#f5efff 100%)' : '#fff',
      border: `1px solid ${highlight ? '#ece4fb' : '#ece9f1'}`,
      borderRadius: '18px',
      p: '18px 20px',
      boxShadow: highlight
        ? '0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(86,40,160,.22)'
        : '0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(20,18,30,.12)',
    }}>
      <Box sx={labelSx}>{label.toUpperCase()}</Box>
      <Box sx={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, color: color || '#161420', mt: '10px' }}>{value}</Box>
    </Box>
  );
}

const BalanceMini = ({ account }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', mt: '16px' }}>
    {CURRENCIES.map((cur) => (
      <Box key={cur} sx={{ background: '#faf9fc', border: '1px solid #f0eef5', borderRadius: '12px', p: '10px 12px' }}>
        <Box sx={labelSx}>{cur}</Box>
        <Box sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: '#161420', mt: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {CUR_SYM[cur]}{fmt(account[cur])}
        </Box>
      </Box>
    ))}
  </Box>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const WalletAccounts = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  useUserRefreshTick(); // re-render when the cached profile refreshes — see hooks/useUserRefreshTick.js
  const _user = getUser();
  const can = (slug) => hasPageAccess(_user, slug);

  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  // Adjust dialog
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ amount: '', currency: 'THB', adjustType: 'add', reason: '' });

  const load = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const result = await getAllAccounts(page, 20, search, statusFilter);
      setAccounts(result.data.accounts);
      setPagination(result.data.pagination);
      setSummary(result.data.summary || { total: 0, active: 0, inactive: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCurrentPage(1); load(1); }, [search, statusFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(currentPage); }, [currentPage]);

  const openAdjust = (e, account) => {
    e.stopPropagation();
    setAdjustTarget(account);
    setAdjustForm({ amount: '', currency: 'THB', adjustType: 'add', reason: '' });
    setAdjustDialog(true);
  };

  const handleAdjust = async () => {
    if (!adjustForm.amount || !adjustForm.reason.trim()) {
      setError(t('wallet.admin.amountAndReasonRequired'));
      return;
    }
    try {
      setAdjustLoading(true);
      setError('');
      const result = await adminAdjustBalance(
        adjustTarget.id,
        parseFloat(adjustForm.amount),
        adjustForm.currency,
        adjustForm.adjustType,
        adjustForm.reason.trim()
      );
      setSuccess(t('wallet.admin.balanceAdjustedSuccess', { type: t(`wallet.admin.${adjustForm.adjustType === 'add' ? 'credited' : 'debited'}`), amount: adjustForm.amount, currency: adjustForm.currency, txnId: result.data.transactionId }));
      setAdjustDialog(false);
      setAccounts((prev) =>
        prev.map((a) => a.id === adjustTarget.id ? { ...a, ...result.data.newBalance } : a)
      );
    } catch (err) {
      setError(err.message || 'Adjustment failed');
    } finally {
      setAdjustLoading(false);
    }
  };

  // Active (+ "self-activated") / Inactive (+ "off by staff" — the customer
  // can't self-activate those). See Account.activatedBy / deactivationReason.
  const statusText = (acct) => {
    if (acct.validflag) {
      return acct.activatedBy?.type === 'self' ? `${t('wallet.admin.active')} · ${t('wallet.admin.selfActivated')}` : t('wallet.admin.active');
    }
    return acct.deactivationReason === 'staff' ? `${t('wallet.admin.inactive')} · ${t('wallet.admin.offByStaff')}` : t('wallet.admin.inactive');
  };

  const handleToggle = async (e, account) => {
    e.stopPropagation();
    try {
      setError('');
      const result = await toggleAccountStatus(account.id);
      setAccounts((prev) =>
        prev.map((a) => a.id === account.id ? { ...a, validflag: result.data.validflag } : a)
      );
      setSuccess(t(result.data.validflag ? 'wallet.admin.accountActivated' : 'wallet.admin.accountDeactivated'));
      load(currentPage);
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  const goToTransactions = (accountId) => navigate(`/wallet/accounts/${accountId}/transactions`);

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1280, mr: 'auto', p: { xs: '20px', md: '28px 40px 60px' } }}>
      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
        <Box>
          <Box component="h1" sx={{ m: 0, fontSize: 26, fontWeight: 800, color: '#16131f', letterSpacing: '-.01em' }}>{t('wallet.admin.accounts')}</Box>
          <Box component="p" sx={{ m: '4px 0 0', fontSize: 13, color: '#9b96a6' }}>{t('wallet.admin.searchAccountOrCustomer')}</Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Box component="button" onClick={() => load(currentPage)} disabled={loading}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: FONT, fontSize: 13, fontWeight: 700, color: '#5b21b6', background: '#fff', border: '1.5px solid #ddd0fa', borderRadius: '11px', padding: '10px 18px', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1, boxShadow: '0 1px 2px rgba(20,18,30,.04)', '&:hover': { background: '#f3f0fc' } }}>
            <Refresh sx={{ fontSize: 16, color: '#6d28d9' }} />{t('common.refresh')}
          </Box>
          <Box component="button" onClick={() => navigate('/wallet/report')}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: FONT, fontSize: 13, fontWeight: 700, color: '#5b21b6', background: '#fff', border: '1.5px solid #ddd0fa', borderRadius: '11px', padding: '10px 18px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(20,18,30,.04)', '&:hover': { background: '#f3f0fc' } }}>
            {t('wallet.admin.backToReport')}
          </Box>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* KPI strip */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3,1fr)' }, gap: '16px', mt: '22px' }}>
        <Kpi highlight label={t('wallet.admin.totalAccounts')} value={fmt(summary.total)} color="#6d28d9" />
        <Kpi label={t('wallet.admin.active')} value={fmt(summary.active)} color="#15a36a" />
        <Kpi label={t('wallet.admin.inactive')} value={fmt(summary.inactive)} color="#6b6775" />
      </Box>

      {/* filter bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '22px', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: '4px', background: '#fff', border: '1px solid #ece9f1', p: '4px', borderRadius: '11px', boxShadow: '0 1px 2px rgba(20,18,30,.04)' }}>
          {STATUSES.map((s) => {
            const active = statusFilter === s;
            return (
              <Box component="button" key={s} onClick={() => setStatusFilter(s)}
                sx={{ fontFamily: FONT, fontSize: '12.5px', fontWeight: 700, padding: '7px 15px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: active ? '#6d28d9' : 'transparent', color: active ? '#fff' : '#7c7787' }}>
                {t(`wallet.admin.${s}`)}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 200, maxWidth: 340 }}>
          <InputBase fullWidth placeholder={t('wallet.admin.pressEnterToSearch')} value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
            sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, px: '14px', py: '9px', borderRadius: '11px', border: '1px solid #ece9f1', background: '#fff', color: '#2c2937', boxShadow: '0 1px 2px rgba(20,18,30,.04)' }} />
        </Box>
        {(searchInput || search) && (
          <Box component="button" onClick={() => { setSearchInput(''); setSearch(''); }}
            sx={{ fontFamily: FONT, fontSize: '12.5px', fontWeight: 700, color: '#7c7787', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            {t('common.clear')}
          </Box>
        )}
        <Box sx={{ ml: 'auto', fontSize: '12.5px', color: '#a8a3b4', fontWeight: 600 }}>
          {t('wallet.admin.accountsFound', { count: pagination.total || 0 })}
        </Box>
      </Box>

      {/* list */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={6}><CircularProgress color="secondary" /></Box>
      ) : accounts.length === 0 ? (
        <Box sx={{ background: '#fff', border: '1px solid #ece9f1', borderRadius: '22px', mt: '16px', p: '50px 24px', textAlign: 'center', color: '#b9b4c4' }}>
          <Box sx={{ fontSize: 32, mb: '10px' }}>🔍</Box>
          <Box sx={{ fontSize: 14, fontWeight: 600, color: '#6b6775' }}>{t('wallet.admin.noAccountsFound')}</Box>
        </Box>
      ) : isMobile ? (
        /* ── Mobile: stacked cards, Branch-Cash style ── */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', mt: '16px' }}>
          {accounts.map((acct, i) => (
            <Box key={acct._id} onClick={() => goToTransactions(acct.id)}
              sx={{ background: '#fff', border: '1px solid #ece9f1', borderRadius: '20px', p: '18px 20px', boxShadow: CARD_SHADOW, cursor: 'pointer', opacity: acct.validflag ? 1 : 0.6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '11px', flex: 'none', background: AVATARS[i % AVATARS.length], color: '#fff', fontFamily: MONO, fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {initials(acct.customer?.name)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: 15, fontWeight: 800, color: '#16131f' }}>{acct.customer?.name || '—'}</Box>
                  <Box sx={{ fontFamily: MONO, fontSize: 11, color: '#9b96a6', mt: '1px' }}>{acct.id}{acct.customer?.whatsapp ? ` · ${acct.customer.whatsapp}` : ''}</Box>
                </Box>
                <ChevronRightRounded sx={{ fontSize: 20, color: '#b9b4c4', ml: 'auto', flex: 'none' }} />
              </Box>
              <BalanceMini account={acct} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '14px' }} onClick={(e) => e.stopPropagation()}>
                <Box sx={{ fontSize: '10.5px', fontWeight: 700, color: acct.validflag ? '#15803d' : '#6b6775', background: acct.validflag ? '#eafaf0' : '#f4f2f8', padding: '4px 10px', borderRadius: '999px' }}>
                  {statusText(acct)}
                </Box>
                <Box sx={{ fontSize: 11, color: '#b3aebd' }}>{formatDate(acct.lastActivityAt || acct.createdAt)}</Box>
                <Box sx={{ display: 'flex', gap: '4px' }}>
                  {can('wallet/accounts/adjust') && (
                    <Tooltip title={t('wallet.admin.adjustBalance')}>
                      <Box component="span" onClick={(e) => openAdjust(e, acct)} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '9px', color: '#6d28d9', cursor: 'pointer', '&:hover': { background: '#f3f0fc' } }}>
                        <TuneOutlined sx={{ fontSize: 18 }} />
                      </Box>
                    </Tooltip>
                  )}
                  <Tooltip title={t(acct.validflag ? 'wallet.admin.deactivate' : 'wallet.admin.activate')}>
                    <Box component="span" onClick={(e) => handleToggle(e, acct)} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: acct.validflag ? '#15a36a' : '#b9b4c4', cursor: 'pointer', '&:hover': { opacity: 0.75 } }}>
                      {acct.validflag ? <ToggleOnOutlined sx={{ fontSize: 28 }} /> : <ToggleOffOutlined sx={{ fontSize: 28 }} />}
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        /* ── Desktop: grid-row table ── */
        <Box sx={{ background: '#fff', border: '1px solid #ece9f1', borderRadius: '22px', overflow: 'hidden', mt: '16px', boxShadow: '0 1px 2px rgba(20,18,30,.04),0 16px 40px -28px rgba(20,18,30,.18)' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 760 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', p: '13px 24px', background: '#faf9fc', borderBottom: '1.5px solid #ece9f1' }}>
                <Box sx={headCell}>{t('wallet.admin.customer')}</Box>
                <Box sx={{ ...headCell, textAlign: 'right' }}>THB</Box>
                <Box sx={{ ...headCell, textAlign: 'right' }}>LAK</Box>
                <Box sx={{ ...headCell, textAlign: 'right' }}>USD</Box>
                <Box sx={{ ...headCell, textAlign: 'right' }}>{t('wallet.admin.lastActivity')}</Box>
                <Box sx={{ ...headCell, textAlign: 'center' }}>Actions</Box>
              </Box>
              {accounts.map((acct, i) => (
                <Box key={acct._id} onClick={() => goToTransactions(acct.id)}
                  sx={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', p: '0 24px', borderTop: '1px solid #f4f2f8', minHeight: 66, cursor: 'pointer', opacity: acct.validflag ? 1 : 0.55, '&:hover': { background: '#faf9fc' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: '14px' }}>
                    <Box sx={{ width: 36, height: 36, flex: 'none', borderRadius: '11px', background: AVATARS[i % AVATARS.length], color: '#fff', fontFamily: MONO, fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {initials(acct.customer?.name)}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ fontSize: 14, fontWeight: 700, color: '#1c1a26', lineHeight: 1.2 }}>{acct.customer?.name || '—'}</Box>
                      <Box sx={{ fontFamily: MONO, fontSize: 11, color: '#9b96a6', mt: '2px' }}>{acct.id}{acct.customer?.whatsapp ? ` · ${acct.customer.whatsapp}` : ''}</Box>
                      <Box sx={{ fontSize: '10.5px', fontWeight: 700, color: acct.validflag ? '#15803d' : '#6b6775', background: acct.validflag ? '#eafaf0' : '#f4f2f8', padding: '2px 7px', borderRadius: '999px', mt: '3px', display: 'inline-block' }}>
                        {statusText(acct)}
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right', fontFamily: MONO, fontSize: 14, fontWeight: acct.THB > 0 ? 700 : 400, color: acct.THB > 0 ? '#161420' : '#b9b4c4' }}>{CUR_SYM.THB}{fmt(acct.THB)}</Box>
                  <Box sx={{ textAlign: 'right', fontFamily: MONO, fontSize: 14, fontWeight: acct.LAK > 0 ? 700 : 400, color: acct.LAK > 0 ? '#161420' : '#b9b4c4' }}>{CUR_SYM.LAK}{fmt(acct.LAK)}</Box>
                  <Box sx={{ textAlign: 'right', fontFamily: MONO, fontSize: 14, fontWeight: acct.USD > 0 ? 700 : 400, color: acct.USD > 0 ? '#161420' : '#b9b4c4' }}>{CUR_SYM.USD}{fmt(acct.USD)}</Box>
                  <Box sx={{ textAlign: 'right', fontSize: 12, color: '#6b6775' }}>{formatDate(acct.lastActivityAt || acct.createdAt)}</Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                    {can('wallet/accounts/adjust') && (
                      <Tooltip title={t('wallet.admin.adjustBalance')}>
                        <Box component="span" onClick={(e) => openAdjust(e, acct)} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '9px', color: '#6d28d9', cursor: 'pointer', '&:hover': { background: '#f3f0fc' } }}>
                          <TuneOutlined sx={{ fontSize: 18 }} />
                        </Box>
                      </Tooltip>
                    )}
                    <Tooltip title={t(acct.validflag ? 'wallet.admin.deactivate' : 'wallet.admin.activate')}>
                      <Box component="span" onClick={(e) => handleToggle(e, acct)} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: acct.validflag ? '#15a36a' : '#b9b4c4', '&:hover': { opacity: 0.75 } }}>
                        {acct.validflag ? <ToggleOnOutlined sx={{ fontSize: 28 }} /> : <ToggleOffOutlined sx={{ fontSize: 28 }} />}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {pagination.pages > 1 && (
        <Box display="flex" justifyContent="center" mt="20px">
          <Pagination count={pagination.pages} page={currentPage} onChange={(_, p) => setCurrentPage(p)} color="secondary" size={isMobile ? 'small' : 'medium'} />
        </Box>
      )}

      {/* Balance Adjustment Dialog */}
      <Dialog open={adjustDialog} onClose={() => !adjustLoading && setAdjustDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ManageAccounts sx={{ color: '#6d28d9' }} />
          {t('wallet.admin.adjustBalance')}
          {adjustTarget && (
            <Box component="span" sx={{ fontSize: 12.5, color: 'text.secondary', display: 'block' }}>
              {adjustTarget.id} — {adjustTarget.customer?.name}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {adjustTarget && <Box mb={2} mt={1}><BalanceMini account={adjustTarget} /></Box>}
          <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
            {t('wallet.admin.authorizedAdjustmentWarning')}
          </Alert>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth label="Amount" type="number" inputProps={{ min: 0.01, step: 0.01 }}
              value={adjustForm.amount}
              onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>{t('wallet.currency')}</InputLabel>
              <Select value={adjustForm.currency} onChange={(e) => setAdjustForm({ ...adjustForm, currency: e.target.value })} label={t('wallet.currency')}>
                {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ gridColumn: '1 / -1' }}>
              <InputLabel>{t('wallet.admin.adjustType')}</InputLabel>
              <Select value={adjustForm.adjustType} onChange={(e) => setAdjustForm({ ...adjustForm, adjustType: e.target.value })} label={t('wallet.admin.adjustType')}>
                <MenuItem value="add">{t('wallet.admin.add')}</MenuItem>
                <MenuItem value="deduct">{t('wallet.admin.deduct')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth multiline rows={3} label={t('wallet.admin.reason')} sx={{ gridColumn: '1 / -1' }}
              value={adjustForm.reason}
              onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
              placeholder="Explain why this adjustment is being made..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setAdjustDialog(false)} disabled={adjustLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color={adjustForm.adjustType === 'add' ? 'success' : 'error'}
            onClick={handleAdjust}
            disabled={adjustLoading || !adjustForm.amount || !adjustForm.reason}
            startIcon={adjustLoading ? <CircularProgress size={18} /> : undefined}
          >
            {adjustLoading ? t('wallet.admin.processing') : `${adjustForm.adjustType === 'add' ? 'Credit' : 'Debit'} ${adjustForm.currency}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletAccounts;
